/**
 * Ensures that the provided function is only run once.
 * @param {Function} fn - The function to be run.
 * @returns {Function} A new function that wraps the original function, ensuring it's only run once.
 */
export function runOnce<T extends Array<unknown>, U>(fn: (...args: T) => U): (...args: T) => U | undefined {
  let hasBeenCalled = false;
  return (...args: T): U | undefined => {
    if (!hasBeenCalled) {
      hasBeenCalled = true;
      return fn(...args);
    }
  };
}

/**
 * Runs the provided function on every call until the first exception occurs.
 * After an exception, the function will not be called anymore.
 * @param {Function} fn - The function to be run.
 * @returns {Function} A new function that wraps the original function, running it until an exception occurs.
 */
export function runUntilException<T extends Array<unknown>, U>(fn: (...args: T) => U): (...args: T) => U | undefined {
  let hasExceptionOccurred = false;
  return (...args: T): U | undefined => {
    if (!hasExceptionOccurred) {
      try {
        return fn(...args);
      } catch (e) {
        hasExceptionOccurred = true;
        throw e;
      }
    }
  };
}
